import { each, map } from "lodash";

import { OUTER, POSTCODE, SECTOR } from "../consts/layers";
import {
  BULK_PLAN_TYPE,
  CHOICE_PLAN_TYPE,
  DEFAULT_PLAN_TYPE,
  LOCATIONS_PLAN_TYPE,
  OVERRIDES_PLAN_TYPE,
  PICKUP_PLAN_TYPE,
  ROBOTS_PLAN_TYPE,
  SMALLS_PLAN_TYPE,
} from "../consts/planTypes";

import { copyOverlayDetails, includeStopBasedOnType } from "./volumeData";
import { getReadyTime, isRedirectionRouteCode } from "./routes";
import { getOverrideMap, getOverrideForStopService } from "./overrides";
import { asyncLoop } from "./async";
import { Postcode } from "./postcode";
import { getPostcodeAllocation } from "./postcodeAllocation";
import { timeBandToInteger } from "./date";
import { getRedirectForPostcode } from "./redirectConfig";
import { getLocationsMap } from "./locations";

export const processRouteStops = async ({
  routes,
  redirectConfig,
  affectedOuter,
  affectedRoutes,
  overlayDetails,
  overrideAreas,
  pickupAreas,
  locationConfig,
  planType,
  collectionType,
}) => {
  const postcodeAllocation = getPostcodeAllocation({ routes, planType });
  if (!overlayDetails || !postcodeAllocation || !routes) return { routes };
  const routesObject = routes.reduce((acc, route) => {
    if (!affectedRoutes) acc[route.routeCode] = route;
    const affectedRoute = affectedRoutes?.find(
      ({ routeCode }) => routeCode === route.routeCode
    );
    if (affectedRoute) acc[route.routeCode] = affectedRoute;
    return acc;
  }, {});

  const overlayDetailsCopy = copyOverlayDetails(overlayDetails);

  await processStopsForOverrides({
    overlayDetails: overlayDetailsCopy,
    affectedOuter,
    routesObject,
    overrideAreas,
  });

  await processStopsForPickup({
    overlayDetails: overlayDetailsCopy,
    routesObject,
    pickupAreas,
  });

  if (!affectedRoutes || LOCATIONS_PLAN_TYPE === planType)
    await processStopsForLocations({
      overlayDetails: overlayDetailsCopy,
      routesObject,
      locationConfig,
    });

  if (
    !affectedRoutes &&
    [SMALLS_PLAN_TYPE, DEFAULT_PLAN_TYPE].includes(planType)
  ) {
    await processStopsForType({
      overlayDetails: overlayDetailsCopy,
      affectedOuter,
      planType: ROBOTS_PLAN_TYPE,
      collectionType,
      routesObject,
      redirectConfig,
      postcodeAllocation: getPostcodeAllocation({
        routes,
        planType: ROBOTS_PLAN_TYPE,
      }),
    });
    await processStopsForType({
      overlayDetails: overlayDetailsCopy,
      affectedOuter,
      planType: CHOICE_PLAN_TYPE,
      collectionType,
      routesObject,
      redirectConfig,
      postcodeAllocation: getPostcodeAllocation({
        routes,
        planType: CHOICE_PLAN_TYPE,
      }),
    });
  } else if (!affectedRoutes && planType === CHOICE_PLAN_TYPE)
    await processStopsForType({
      overlayDetails: overlayDetailsCopy,
      affectedOuter,
      planType: ROBOTS_PLAN_TYPE,
      collectionType,
      routesObject,
      redirectConfig,
      postcodeAllocation: getPostcodeAllocation({
        routes,
        planType: ROBOTS_PLAN_TYPE,
      }),
    });

  if (!affectedRoutes && planType === DEFAULT_PLAN_TYPE) {
    await processStopsForType({
      overlayDetails: overlayDetailsCopy,
      affectedOuter,
      planType: SMALLS_PLAN_TYPE,
      collectionType,
      routesObject,
      redirectConfig,
      postcodeAllocation: getPostcodeAllocation({
        routes,
        planType: SMALLS_PLAN_TYPE,
      }),
    });
    await processStopsForType({
      overlayDetails: overlayDetailsCopy,
      affectedOuter,
      planType: BULK_PLAN_TYPE,
      collectionType,
      routesObject,
      redirectConfig,
      postcodeAllocation: getPostcodeAllocation({
        routes,
        planType: BULK_PLAN_TYPE,
      }),
    });
  }

  if (
    ![OVERRIDES_PLAN_TYPE, LOCATIONS_PLAN_TYPE, PICKUP_PLAN_TYPE].includes(
      planType
    )
  )
    await processStopsForType({
      overlayDetails: overlayDetailsCopy,
      affectedOuter,
      planType,
      collectionType,
      routesObject,
      redirectConfig,
      postcodeAllocation,
    });

  return {
    routes: Object.values(routesObject),
    overlayDetails: overlayDetailsCopy,
  };
};

const processStopsForLocations = async ({
  overlayDetails = [],
  routesObject,
  locationConfig,
}) => {
  const locationsMap = getLocationsMap(locationConfig);
  await asyncLoop(overlayDetails, "forEach", (overlayTimeBand) => {
    overlayTimeBand.overlayStops = map(overlayTimeBand.overlayStops, (stop) => {
      if (
        stop.locationInvalid ||
        [OVERRIDES_PLAN_TYPE, PICKUP_PLAN_TYPE].includes(stop.stopType)
      )
        return stop;

      const location = locationsMap[stop.locationCode];
      if (!location) return stop;

      const route = routesObject[location.routeCode];
      if (!route) return stop;
      const allocatedStop = {
        ...stop,
        routeCode: route.routeCode,
        stopType: LOCATIONS_PLAN_TYPE,
      };
      if (!route.stops) route.stops = [];
      route.stops = [...route.stops, allocatedStop];
      return allocatedStop;
    });
  });
};
const processStopsForPickup = async ({
  overlayDetails = [],
  routesObject,
  pickupAreas,
}) => {
  if (!pickupAreas) return;
  await asyncLoop(overlayDetails, "forEach", (overlayTimeBand) => {
    overlayTimeBand.overlayStops = map(overlayTimeBand.overlayStops, (stop) => {
      if (
        stop.postcodeInvalid ||
        !stop.partnerType ||
        stop.stopType === OVERRIDES_PLAN_TYPE
      )
        return stop;

      const postcode = new Postcode(stop.postcode);
      let partnerRouteCode =
        pickupAreas.POSTCODE[postcode.toPostcode()]?.[stop.partnerType];
      if (!partnerRouteCode)
        partnerRouteCode =
          pickupAreas.SECTOR[postcode.toSector()]?.[stop.partnerType];
      if (!partnerRouteCode)
        partnerRouteCode =
          pickupAreas.OUTER[postcode.toOuter()]?.[stop.partnerType];

      const route = routesObject[partnerRouteCode];
      if (!route) return stop;
      const allocatedStop = {
        originalStopType: stop.stopType,
        ...stop,
        routeCode: route.routeCode,
        stopType: PICKUP_PLAN_TYPE,
      };
      if (!route.stops) route.stops = [];
      route.stops = [...route.stops, allocatedStop];
      return allocatedStop;
    });
  });
};

const processStopsForOverrides = async ({
  overlayDetails = [],
  affectedOuter,
  routesObject,
  overrideAreas,
}) => {
  const overrideMap = getOverrideMap(overrideAreas);
  await asyncLoop(overlayDetails, "forEach", (overlayTimeBand) => {
    overlayTimeBand.overlayStops = map(overlayTimeBand.overlayStops, (stop) => {
      if (!!!stop.postcode || stop.postcodeInvalid) return stop;
      const postcode = new Postcode(stop.postcode);
      if (affectedOuter && postcode.toOuter() !== affectedOuter) return stop;

      let override = null;
      for (const level of [POSTCODE, SECTOR, OUTER]) {
        if (override) break;
        override = getOverrideForStopService(
          overrideMap[postcode.getPostcode(level)],
          stop
        );
      }
      const route = routesObject[override?.routeCode];
      if (!route) return stop;
      const allocatedStop = {
        originalStopType: stop.stopType,
        ...stop,
        routeCode: route.routeCode,
        stopType: OVERRIDES_PLAN_TYPE,
      };
      if (!route.stops) route.stops = [];
      route.stops = [...route.stops, allocatedStop];
      return allocatedStop;
    });
  });
};

const processStopsForType = async ({
  overlayDetails = [],
  affectedOuter,
  planType,
  collectionType,
  routesObject,
  redirectConfig,
  postcodeAllocation,
}) => {
  await asyncLoop(overlayDetails, "forEach", (overlayTimeBand) => {
    let timeBand = overlayTimeBand.timeBand;
    if (timeBand.startsWith("23:")) timeBand = `-${timeBand}`;
    else if (timeBand > "16:00") timeBand = `-23:00`;

    overlayTimeBand.overlayStops = map(overlayTimeBand.overlayStops, (stop) => {
      if (!!!stop.postcode || stop.postcodeInvalid) return stop;
      const postcode = new Postcode(stop.postcode);
      if (affectedOuter && postcode.toOuter() !== affectedOuter) return stop;

      if (!includeStopBasedOnType({ planType, stop })) return stop;
      stop = { ...stop, timeBand: overlayTimeBand.timeBand };
      const routesForPostcode = getRoutesFromPostcodeAllocation(
        postcodeAllocation,
        postcode
      );

      let allocated = false;
      allocated = processStopsForLevel({
        routes: routesForPostcode.POSTCODE || [],
        postcode,
        routesObject,
        redirectConfig,
        stop,
        timeBand,
        level: POSTCODE,
        planType,
        collectionType,
      });
      if (!!allocated)
        return {
          ...stop,
          routeCode: allocated?.routeCode,
          routeType: allocated?.stopType,
        };

      allocated = processStopsForLevel({
        routes: routesForPostcode.SECTOR || [],
        postcode,
        routesObject,
        redirectConfig,
        stop,
        timeBand,
        level: SECTOR,
        planType,
        collectionType,
      });
      if (!!allocated)
        return {
          ...stop,
          routeCode: allocated?.routeCode,
          routeType: allocated?.stopType,
        };
      allocated = processStopsForLevel({
        routes: routesForPostcode.OUTER || [],
        postcode,
        routesObject,
        redirectConfig,
        stop,
        timeBand,
        level: OUTER,
        planType,
        collectionType,
      });
      return {
        ...stop,
        routeCode: allocated?.routeCode,
        routeType: allocated?.stopType,
      };
    });
  });
};

const processStopsForLevel = ({
  routes,
  routesObject,
  redirectConfig,
  postcode,
  timeBand,
  stop,
  level = POSTCODE,
  planType,
  collectionType,
}) => {
  const mapping = processStopsForLevelMapping(postcode)[level];
  let allocated = false;
  routes.forEach((routeCode) => {
    const route = routesObject[routeCode];
    if (!route || !!allocated) return;
    route[mapping.routeKey].forEach((routePostcode) => {
      if (routePostcode[mapping.postcodeKey] !== mapping.getPostcode) return;

      const startTime = routePostcode.startTime.replace("23:", "-23:");
      const readyTime = getReadyTime(route, planType, collectionType);

      const timeBandInteger = timeBandToInteger(timeBand);
      if (
        timeBandInteger >= timeBandToInteger(startTime) &&
        timeBandInteger <= timeBandToInteger(readyTime)
      ) {
        let allocationRoute = route;
        if (isRedirectionRouteCode(routeCode)) {
          routePostcode = null;
          const redirectToRouteCode = getRedirectForPostcode({
            redirectConfig,
            redirectionRouteCode: routeCode,
            postcode: mapping.getPostcode,
          });
          allocationRoute = routesObject[redirectToRouteCode];

          if (allocationRoute) {
            if (level === POSTCODE)
              routePostcode = allocationRoute.postcode.find(
                (p) => p.postcode === postcode.toPostcode()
              );
            if (!routePostcode && level !== OUTER)
              routePostcode = allocationRoute.sector.find(
                ({ postcodeSector }) => postcodeSector === postcode.toSector()
              );
            if (!routePostcode)
              routePostcode = allocationRoute.outer.find(
                ({ postcodeOuter }) => postcodeOuter === postcode.toOuter()
              );
          }
        }

        if (routePostcode)
          routePostcode.stopsCount = routePostcode.stopsCount
            ? routePostcode.stopsCount + 1
            : 1;
        if (allocationRoute && allocationRoute.stops) {
          allocationRoute.stops = [
            ...allocationRoute.stops,
            { ...stop, timeBand },
          ];
        } else if (allocationRoute) {
          allocationRoute.stops = [{ ...stop, timeBand }];
        }
        allocated = {
          routeCode: allocationRoute?.routeCode,
          stopType: allocationRoute?.routeType,
        };
        return;
      }
      return;
    });
  });
  return allocated;
};

const processStopsForLevelMapping = (postcode) => ({
  POSTCODE: {
    routeKey: "postcode",
    postcodeKey: "postcode",
    getPostcode: postcode.toPostcode(),
  },
  SECTOR: {
    routeKey: "sector",
    postcodeKey: "postcodeSector",
    getPostcode: postcode.toSector(),
  },
  OUTER: {
    routeKey: "outer",
    postcodeKey: "postcodeOuter",
    getPostcode: postcode.toOuter(),
  },
});

const getRoutesFromPostcodeAllocation = (postcodeAllocation, postcode) => {
  const allocations = { POSTCODE: [], SECTOR: [], OUTER: [] };

  each(Object.keys(postcodeAllocation), (key) => {
    const [outer] = key.split(" ");
    if (outer !== postcode.toOuter()) return;

    allocations[Postcode.getLevel(key)].push(...postcodeAllocation[key]);
  });
  return allocations;
};

export const getVolume = ({
  postcode,
  level = "overlayOuter",
  volumeTimeBand,
  lastRouteTimeBand,
  overlayDetails,
}) => {
  const endStopsVolume =
    overlayDetails[volumeTimeBand]?.[level]?.[postcode]?.stopsCumulativeTotal ||
    0;
  const startStopsVolume =
    overlayDetails[lastRouteTimeBand]?.[level]?.[postcode]
      ?.stopsCumulativeTotal || 0;
  const endParcelsVolume =
    overlayDetails[volumeTimeBand]?.[level]?.[postcode]
      ?.parcelsCumulativeTotal || 0;
  const startParcelsVolume =
    overlayDetails[lastRouteTimeBand]?.[level]?.[postcode]
      ?.parcelsCumulativeTotal || 0;
  return {
    stops: endStopsVolume - startStopsVolume,
    parcels: endParcelsVolume - startParcelsVolume,
  };
};
